import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Clock, BadgeCheck, Hourglass, Gift, Copyright } from "lucide-react";
import { Header } from "@/pages/Operations";
import { PackCard } from "@/components/billing/PackCard";
import { UpiCheckout } from "@/components/billing/UpiCheckout";
import { OrderHistory } from "@/components/billing/OrderHistory";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const { t } = useT();
  const [packs, setPacks] = useState([]);
  const [me, setMe] = useState({ subscription: null, orders: [] });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [referral, setReferral] = useState("");
  const [license, setLicense] = useState("standard");
  const autoStarted = useRef(false);

  const refresh = useCallback(() => api.get("/billing/me").then((r) => setMe(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/billing/packs").then((r) => setPacks(r.data.packs)).catch(() => {});
    refresh();
  }, [refresh]);
  const hasPending = me.orders.some((o) => o.status === "pending_verification");
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [hasPending, refresh]);
  const lastOrder = me.orders[0];

  const choose = useCallback(async (pack, lic) => {
    setBusy(true);
    try {
      const r = await api.post("/billing/orders", { pack_id: pack.id, referral_code: referral.trim() || null, license: lic || license });
      setOrder(r.data);
      if (r.data.referral_code) toast.success(`Referral ${r.data.referral_code} applied — +7 days on activation`);
      refresh();
      setTimeout(() => document.getElementById("upi-checkout-anchor")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create order"); }
    finally { setBusy(false); }
  }, [refresh, referral, license]);

  useEffect(() => {
    const pid = params.get("pack");
    if (!pid || !packs.length || autoStarted.current) return;
    const pack = packs.find((p) => p.id === pid);
    const lic = params.get("license");
    if (lic === "copyright_free") setLicense(lic);
    if (pack) { autoStarted.current = true; choose(pack, lic); setParams({}, { replace: true }); }
  }, [params, packs, choose, setParams]);

  const resume = async (o) => {
    try { const r = await api.get(`/billing/orders/${o.order_id}`); setOrder(r.data); }
    catch { toast.error("Could not load order"); }
  };

  const sub = me.subscription;
  const pending = me.orders.find((o) => o.status === "pending_verification");
  const renewalDue = sub && daysLeft(sub.expires_at) <= 3;

  return (
    <div data-testid="billing-page" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <Header icon={CreditCard} eyebrow="Prepaid Packs · UPI" title={t("Power up your studio")} subtitle={t("Prepaid, no auto-debit. Pay via UPI, submit your UTR, and your pack activates after a quick verification.")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div data-testid="current-pack" className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${sub ? "bg-emerald-500/15" : "bg-white/5"}`}>
            {sub ? <BadgeCheck size={22} className="text-emerald-400" /> : <Clock size={22} className="text-slate-500" />}
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("Current pack")}</div>
            <div className="font-display font-bold text-lg">{sub ? `${sub.pack_name} · ${daysLeft(sub.expires_at)} days left` : t("No active pack")}</div>
            <div className="text-xs text-slate-500">{sub ? `Valid till ${new Date(sub.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : "Choose a prepaid pack below to unlock the arsenal."}</div>
          </div>
        </div>
        {renewalDue && !pending && (
          <div data-testid="renewal-banner" className="rounded-2xl p-5 border border-amber-500/30 bg-amber-500/5 flex items-center gap-4">
            <Clock size={22} className="text-amber-400" />
            <div className="flex-1">
              <div className="font-display font-semibold">Your {sub.pack_name} pack renews in {daysLeft(sub.expires_at)} day{daysLeft(sub.expires_at) === 1 ? "" : "s"}</div>
              <div className="text-xs text-slate-400">Renew now with one tap — validity extends from your current expiry, nothing is lost.</div>
            </div>
            <button data-testid="renew-now" onClick={() => choose(packs.find((p) => p.id === sub.pack_id) || packs[0], sub.license)} className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold">Renew via UPI</button>
          </div>
        )}
        {pending && (
          <div data-testid="pending-banner" className="rounded-2xl p-5 border border-amber-500/30 bg-amber-500/5 flex items-center gap-4">
            <Hourglass size={22} className="text-amber-400 animate-pulse" />
            <div>
              <div className="font-display font-semibold">Payment under verification</div>
              <div className="text-xs text-slate-400">UTR {pending.utr} for {pending.pack_name} (₹{pending.amount}) is being verified. This page refreshes automatically every 15s — your pack activates here the moment it is approved.</div>
            </div>
          </div>
        )}
        {lastOrder && (
          <div data-testid="order-status-timeline" className="glass rounded-2xl p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Latest order · {lastOrder.pack_name} · ₹{lastOrder.amount}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {[["created", "Order created"], ["pending_verification", "UTR submitted · verifying"], ["approved", "Pack active"]].map(([k, l], i) => {
                const order = ["created", "pending_verification", "approved"];
                const cur = lastOrder.status === "approved" ? 2 : lastOrder.status === "pending_verification" ? 1 : 0;
                const done = order.indexOf(k) <= cur && lastOrder.status !== "rejected" && lastOrder.status !== "expired";
                return <span key={k} data-testid={`order-step-${k}`} className={`px-2.5 py-1 rounded-full border font-mono ${done ? "border-emerald-500/40 text-emerald-300" : "border-white/10 text-slate-500"}`}>{i + 1}. {l}</span>;
              })}
              {lastOrder.status === "rejected" && <span data-testid="order-rejected" className="px-2.5 py-1 rounded-full border border-red-500/40 text-red-300 font-mono">Rejected: {lastOrder.reject_reason || "UTR could not be verified"} — resubmit the correct UTR below</span>}
              {lastOrder.status === "expired" && <span className="px-2.5 py-1 rounded-full border border-white/10 text-slate-500 font-mono">Expired — start a new order</span>}
              {lastOrder.status === "created" && <span className="text-slate-400">Pay via the UPI QR / ID, then enter the 12-digit UTR from your UPI app to move to step 2.</span>}
            </div>
          </div>
        )}
      </div>

      <div data-testid="license-toggle" className="glass rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Copyright size={13} className="text-amber-400" /> License</div>
        {[["standard", "Standard", "Personal & creator use · 'Made with Vectra' credit"], ["copyright_free", "Copyright-Free", "Full commercial rights · copyright removed · no attribution"]].map(([k, l, d]) => (
          <button key={k} data-testid={`license-${k}`} onClick={() => setLicense(k)} className={`text-left rounded-xl border px-4 py-2 transition-colors ${license === k ? "border-amber-400/50 bg-amber-500/10" : "border-white/10 hover:bg-white/5"}`}>
            <div className="text-sm font-semibold">{l}</div><div className="text-[10px] text-slate-500">{d}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs text-slate-500 flex items-center gap-1.5"><Gift size={13} className="text-pink-400" /> Have a referral code? Get +7 days on your first pack.</div>
        <input data-testid="referral-input" value={referral} onChange={(e) => setReferral(e.target.value.toUpperCase())} placeholder="VEC-XXXXXX" maxLength={10}
          className="bg-[#0A0C14] border border-white/10 rounded-lg px-3 py-1.5 text-xs font-mono outline-none focus:border-purple-500/40 placeholder:text-slate-600 w-36" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {packs.map((p, i) => <PackCard key={p.id} pack={p} index={i} current={sub?.pack_id === p.id} busy={busy} onChoose={choose} license={license} />)}
      </div>

      <div id="upi-checkout-anchor" className="mb-6">
        {order && <UpiCheckout order={order} onClose={() => setOrder(null)} onSubmitted={() => { setOrder(null); refresh(); }} />}
      </div>

      <OrderHistory orders={me.orders} onResume={resume} />
    </div>
  );
}
